import { createFileRoute } from '@tanstack/react-router';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/use-form — live docs for the `useForm()` form-state hook.
 *
 * See the /ui/form page for live demos of useForm in both
 * controlled and uncontrolled modes. This page focuses on the API
 * surface and canonical usage patterns.
 */
export const Route = createFileRoute('/ui/_layout/use-form')({
  component: UseFormPage,
});

function UseFormPage() {
  return (
    <Stack gap={6}>
      <Section
        title="useForm"
        hint="form-state hook — values, errors, touched, dirty, handleChange, handleBlur, handleSubmit, reset"
      >
        <Text>
          See the <a href="/ui/form">/ui/form</a> page for live demos of <code>useForm</code> in
          both controlled and uncontrolled modes.
        </Text>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            title="Uncontrolled — minimal"
            hint="Inputs carry a name; useForm reads values + errors + touched"
            language="tsx"
            code={`const form = useForm({
  initialValues: { email: '', password: '' },
  validate: (v) => {
    const e: Record<string, string> = {};
    if (!v.email.includes('@')) e.email = 'Enter a valid email.';
    if (v.password.length < 12) e.password = 'At least 12 characters.';
    return e;
  },
  onSubmit: async (values) => api.auth().login(values),
});

return (
  <form onSubmit={form.handleSubmit}>
    <Field
      label="Email"
      error={form.touched.email ? form.errors.email : null}
    >
      <Input
        name="email"
        value={form.values.email}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
      />
    </Field>
    <Button type="submit" disabled={form.submitting}>Sign in</Button>
  </form>
);`}
          />

          <CodeBlock
            title="Server error + reset"
            language="tsx"
            code={`const [serverError, setServerError] = useState<string | null>(null);
const form = useForm({
  initialValues: { email: '', password: '' },
  onSubmit: async (values) => {
    try {
      await api.auth().login(values);
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'Login failed');
    }
  },
});

// In the JSX:
{serverError && <Alert variant="danger">{serverError}</Alert>}
<Button
  type="button"
  onClick={() => {
    form.reset();
    setServerError(null);
  }}
>
  Reset
</Button>`}
          />

          <CodeBlock
            title="Field-level error from the server"
            hint="Use setFieldError when the API returns per-field validation errors"
            language="tsx"
            code={`const form = useForm({
  initialValues: { email: '', password: '' },
  onSubmit: async (values) => {
    try {
      await api.auth().register(values);
    } catch (e) {
      const issues = e instanceof ApiError ? e.issues : [];
      for (const issue of issues) {
        form.setFieldError(issue.field, issue.message);
      }
    }
  },
});`}
          />
        </Stack>
      </Section>
    </Stack>
  );
}
